import React, { ReactNode, useEffect, useRef } from 'react';

import { IconTripleDot } from '../icons/IconTripleDot';

import './context-menu.css';

interface Props {
    addClasses?: string;
    children: ReactNode;
    disabled?: boolean;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'top-left';
    setShowChildren(showChildrenState: boolean): void;
    showChildren: boolean;
}

/**
 * Displays a contextual menu when the user clicks on an icon. Similar to a Tooltip, but with a few
 * differences:
 * - displays its children when the menu is open
 * - leaves `showChildren` state management to its parent
 * - reacts to clicks instead of hovers
 * - always uses an <IconTripleDot /> as the menu trigger
 */
export const ContextMenu = ({
    children,
    disabled = false,
    position = 'top',
    setShowChildren,
    showChildren,
}: Props): JSX.Element => {
    const triggerEl = useRef<HTMLButtonElement>(null);
    const dialogEl = useRef<HTMLDivElement>(null);

    // Close the menu when the user clicks outside of it
    useEffect(() => {
        const clickListener = (e: MouseEvent): void => {
            const clickedElement = e.target as HTMLElement;

            if (
                !clickedElement.classList.contains(
                    'no-context-menu-click-listener',
                ) &&
                triggerEl.current !== null &&
                !triggerEl.current.contains(clickedElement) &&
                dialogEl.current !== null &&
                !dialogEl.current.contains(clickedElement)
            ) {
                setShowChildren(false);
            }
        };

        document.addEventListener('click', clickListener);

        return (): void => {
            document.removeEventListener('click', clickListener);
        };
    });

    return (
        <span className="context-menu relative">
            {showChildren && (
                <div
                    className={`context-menu-bubble max-w-xl absolute z-10 context-menu-${position}`}
                >
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <div
                        ref={dialogEl}
                        className="context-menu-dialog bg-white shadow-xl respond-to-mouse"
                    >
                        {children}
                    </div>
                </div>
            )}
            <button
                disabled={disabled}
                ref={triggerEl}
                className="context-menu-trigger respond-to-mouse"
                type="button"
                onClick={(): void => setShowChildren(true)}
            >
                <IconTripleDot fillColor="tertiary-300" />
            </button>
        </span>
    );
};
